/**
 * Level 2 records-by-band — three tiles, each individually clickable to open its
 * records for the scoped selection. High carries a berry accent.
 * The list is an access point into the records behind each band, not a chart.
 *
 * Each band tile shows a cheap top-N sample straight from the detail payload.
 * "View more" pages the FULL band (worst first) via useBandRecords and appends —
 * so a band with hundreds of records is fully reachable, not capped at the sample.
 */

import { useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useNavigation } from '../../../../../contexts/NavigationContext';
import { ConductScoreCircle } from '../../../../ui/conduct-score-circle';
import type { FilterConfig } from '../../../records/filter';
import { BAND_COLORS, BAND_LABELS, BAND_RANGE } from '../constants';
import { useBandRecords } from '../hooks/useBandRecords';
import type { Band, BandRecords, Bands, Framework, HighBandRecord } from '../types';

const BAND_ORDER: Band[] = ['low', 'mod', 'high'];

export function BandTiles({
  bands,
  bandRecords,
  datasetId,
  framework,
  outcomeId,
  filters,
  bucketStart,
  bucketEnd,
}: {
  bands: Bands;
  bandRecords: BandRecords;
  datasetId: string;
  framework: Framework;
  outcomeId: string;
  filters: FilterConfig;
  /** Selected trend-chart bucket window [start, end). Null = whole period.
   *  Paging scopes to this so "View more" matches the bucket-scoped tile. */
  bucketStart: string | null;
  bucketEnd: string | null;
}) {
  const { navigateWithReferrer } = useNavigation();
  const location = useLocation();
  const [openBand, setOpenBand] = useState<Band | null>(null);
  // Lazy: nothing is fetched until the reader asks for more than the sample.
  const [expanded, setExpanded] = useState(false);

  const sample: HighBandRecord[] =
    openBand && bandRecords[openBand].length ? bandRecords[openBand] : [];
  const bandCount = openBand ? Math.round(bands[openBand]) : 0;
  // When the payload sample is empty but the band has records — common under a
  // chart-bucket selection, where none of the period's top-20 fall in the
  // bucket — fetch the first page immediately so those records stay reachable.
  const autoFetch = openBand != null && bandCount > 0 && sample.length === 0;
  const active = expanded || autoFetch;

  const {
    records: pagedRecords,
    total,
    hasMore,
    loadMore,
    isLoading,
    isLoadingMore,
    isError,
  } = useBandRecords(
    datasetId, framework, outcomeId, openBand, filters,
    active && openBand != null, bucketStart, bucketEnd,
  );

  // Once a page has arrived, the paged list (a superset of the sample, same
  // order + same bucket scope) takes over; until then the sample shows instantly.
  const records = active && pagedRecords.length ? pagedRecords : sample;
  const shownTotal = active && pagedRecords.length ? total : bandCount;
  const moreToLoad = active ? hasMore : bandCount > sample.length;
  const showRecordsBlock = openBand != null && bandCount > 0;

  function toggleBand(k: Band, isOpen: boolean) {
    setOpenBand(isOpen ? null : k);
    setExpanded(false);
  }

  function onViewMore() {
    if (!active) setExpanded(true);
    else loadMore();
  }

  return (
    <div>
      <div className="mt-0 flex items-center justify-between border-b border-[var(--soft,#ECECEE)] pb-2 text-[9px] font-extrabold uppercase tracking-[0.08em] text-muted-foreground">
        <span>Records by band</span>
        <span>Records</span>
      </div>

      <div className="mt-3.5 grid grid-cols-1 gap-3 md:grid-cols-3">
        {BAND_ORDER.map((k) => {
          const isHigh = k === 'high';
          const count = Math.round(bands[k]);
          // Openable whenever the band has records — the sample may be empty
          // under a bucket selection, but the page fetch will reach them.
          const canOpen = count > 0;
          const isOpen = openBand === k && canOpen;
          const cls = `w-full rounded-[var(--radius-sm)] border p-3.5 text-left transition-all ${
            canOpen ? 'cursor-pointer hover:shadow-[var(--shadow-card)]' : ''
          }`;
          const style = {
            borderColor: isHigh ? 'rgba(157,11,77,.35)' : 'hsl(var(--border))',
            background: isHigh ? 'rgba(157,11,77,.03)' : undefined,
            ...(isOpen ? { outline: `1.5px solid ${BAND_COLORS[k]}`, outlineOffset: '-1px' } : {}),
          };
          const inner = (
            <>
              <div className="flex items-center gap-1.5 text-[11px] font-extrabold text-muted-foreground">
                <i className="h-2 w-2 rounded-full" style={{ background: BAND_COLORS[k] }} />
                {BAND_LABELS[k]}
              </div>
              <div className="my-1 text-2xl font-extrabold tracking-[-0.03em]">
                {count.toLocaleString('en-GB')}
              </div>
              <div className="text-[10px] font-semibold text-muted-foreground">{BAND_RANGE[k]}</div>
              <div className="mt-1 text-[10px] font-extrabold" style={{ color: canOpen ? BAND_COLORS[k] : undefined }}>
                {count === 0 ? (
                  <span className="text-muted-foreground">None in this period</span>
                ) : isOpen ? (
                  'Hide records ×'
                ) : (
                  'View records →'
                )}
              </div>
            </>
          );
          return canOpen ? (
            <button
              key={k}
              type="button"
              onClick={() => toggleBand(k, isOpen)}
              className={cls}
              style={style}
            >
              {inner}
            </button>
          ) : (
            <div key={k} className={cls} style={style}>
              {inner}
            </div>
          );
        })}
      </div>

      {showRecordsBlock && (
        <div className="mt-4 space-y-2 border-t border-[var(--soft,#ECECEE)] pt-3">
          {records.length > 0 && shownTotal > records.length && (
            <div className="pb-1 text-[10px] font-semibold text-muted-foreground">
              Showing {records.length.toLocaleString('en-GB')} of {shownTotal.toLocaleString('en-GB')}
            </div>
          )}
          {records.map((r) => (
            <button
              key={r.id}
              type="button"
              // Preserve the full report URL (outcome/grain/plot/bucket live in
              // the query string) as the referrer, so the record's Back returns
              // to this drill-down, not the report overview.
              onClick={() =>
                navigateWithReferrer(
                  `/v4/records/${datasetId}/${r.id}`,
                  `${location.pathname}${location.search}`,
                )
              }
              className="flex w-full items-center gap-3 rounded-lg px-1 py-1.5 text-left transition-colors hover:bg-muted"
            >
              <ConductScoreCircle score={r.score} size="sm" showDecimal />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-semibold">{r.headline}</div>
                <div className="mt-0.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                  <span className="truncate">
                    {[r.channel, r.occurredAt].filter(Boolean).join(' · ')}
                  </span>
                  <span className="ml-auto shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold">
                    {r.outcomeName}
                  </span>
                </div>
              </div>
            </button>
          ))}

          {active && isLoading && records.length === 0 && (
            <div className="py-2 text-[11px] font-semibold text-muted-foreground">
              Loading records…
            </div>
          )}

          {isError && (
            <div className="py-1 text-[11px] font-semibold text-destructive">
              Couldn’t load more records. Try again.
            </div>
          )}

          {moreToLoad && (
            <button
              type="button"
              onClick={onViewMore}
              disabled={isLoading || isLoadingMore}
              className="w-full rounded-lg py-2 text-[11px] font-extrabold text-muted-foreground transition-colors hover:bg-muted disabled:opacity-60"
              style={{ color: openBand ? BAND_COLORS[openBand] : undefined }}
            >
              {isLoading || isLoadingMore ? 'Loading…' : 'View more'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
